import ProgressBar from 'components/molecules/ProgressBar/ProgressBar';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@spotted-zebra-uk/ui-components';
import EnglishProficiencyQuestionCard from './EnglishProficiencyQuestionCard';
import {
  countAnsweredQuestions,
  TEnglishProficiencyAnswers,
  TEnglishProficiencyQuestion,
} from './EnglishProficiencyTest.helpers';
import styles from './EnglishProficiencyTest.module.scss';

interface IEnglishProficiencyForm {
  questions: TEnglishProficiencyQuestion[];
  answers: TEnglishProficiencyAnswers;
  onChooseAnswer: (questionIndex: number, selectedAnswerIndex: number) => void;
  onTypeBlanks: (questionIndex: number, blankAnswers: string[]) => void;
  onBlurBlanks: (questionIndex: number) => void;
  disabled: boolean;
  /** Left of the header. The clock on a real sitting; a label on a practice
   * one, which has no deadline to show. */
  lead: ReactNode;
  submitLabel: string;
  onSubmit: () => void;
  /** Anything to say above the questions — an unsaved-answers warning, say. */
  notice?: ReactNode;
}

/**
 * The page a candidate answers on.
 *
 * Shared by the real sitting and the practice one so that practising is
 * practising the thing itself: same header, same cards, same gaps, same button
 * in the same place. What differs between them — a clock, a queue of saves, a
 * confirmation before the irreversible bit — is owned by the caller, and is
 * exactly the set of things a practice run has no business having.
 */
const EnglishProficiencyForm: FC<IEnglishProficiencyForm> = ({
  questions,
  answers,
  onChooseAnswer,
  onTypeBlanks,
  onBlurBlanks,
  disabled,
  lead,
  submitLabel,
  onSubmit,
  notice,
}) => {
  const { t } = useTranslation();
  const answeredCount = countAnsweredQuestions(questions, answers);

  return (
    <>
      <div className={styles.header}>
        <div className={styles.headerInner}>
          {lead}
          <div className={styles.progress}>
            <p className={styles.progressLabel}>
              {t('englishProficiencyTest.answeredCount', {
                answered: answeredCount,
                total: questions.length,
              })}
            </p>
            <div className={styles.progressBar}>
              <ProgressBar value={answeredCount} maxValue={questions.length} />
            </div>
          </div>
          {/* In the header rather than under the last question: the candidate
              answers in whatever order they like, so the end of the form is
              not where they finish. */}
          <Button
            className={styles.submitButton}
            onClick={onSubmit}
            disabled={disabled}
            data-testid="english-proficiency-test--submit"
          >
            {submitLabel}
          </Button>
        </div>
      </div>

      <div className={styles.test}>
        {notice}

        <ol className={styles.questions}>
          {questions.map((question, position) => (
            <EnglishProficiencyQuestionCard
              key={question.index}
              question={question}
              answer={answers[question.index]}
              position={position + 1}
              total={questions.length}
              onChooseAnswer={onChooseAnswer}
              onTypeBlanks={onTypeBlanks}
              onBlurBlanks={onBlurBlanks}
              disabled={disabled}
            />
          ))}
        </ol>
      </div>
    </>
  );
};

export default EnglishProficiencyForm;
